import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  LoadingSkeleton,
  Modal,
  Pagination,
} from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import { useBankAccounts } from './useBankAccounts';
import {
  useScheduledPayments,
  useConfirmFinancialEntry,
  useTransmitPayment,
} from './treasury/treasuryHooks';
import {
  PAYMENT_STATUS_LABEL,
  PAYMENT_CHANNEL_LABEL,
  type ScheduledPayment,
} from './treasury/treasuryTypes';

/* ── Contratos reais (backend/.review-loop/reports/served-routes.json) ──
 * GET  /finance/payments?statusIn&channel&q&page&pageSize → PaginatedResponseDto<ScheduledPaymentResponseDto>
 * POST /finance/payments/:id/transmit  → ScheduledPaymentResponseDto (TransmitPaymentDto { batchId })
 * POST /finance/payments/:id/confirm   → ConfirmFinancialEntryResultDto
 *   corpo: ConfirmFinancialEntryDto { accountId, notes? }
 * (DTOs em backend/.../dto/treasury.dto.ts)
 */

function paymentStatusBadge(status: ScheduledPayment['status']) {
  const map: Record<ScheduledPayment['status'], { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }> = {
    SCHEDULED: { variant: 'info', label: 'Agendado' },
    TRANSMITTED: { variant: 'warning', label: 'Transmitido' },
    CONFIRMED: { variant: 'success', label: 'Confirmado' },
    FAILED: { variant: 'error', label: 'Falhou' },
    CANCELLED: { variant: 'neutral', label: 'Cancelado' },
  };
  return map[status];
}

const STATUS_FILTERS = [
  { value: 'ALL', label: 'Todos' },
  { value: 'SCHEDULED', label: 'Agendado' },
  { value: 'TRANSMITTED', label: 'Transmitido' },
  { value: 'CONFIRMED', label: 'Confirmado' },
  { value: 'FAILED', label: 'Falhou' },
];

export function PaymentConfirmPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [confirmTarget, setConfirmTarget] = useState<ScheduledPayment | null>(null);

  const paymentsQ = useScheduledPayments({
    page,
    pageSize: 20,
    ...(statusFilter !== 'ALL' ? { statusIn: [statusFilter] } : {}),
  });

  const transmitMutation = useTransmitPayment();

  const data = paymentsQ.data;
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / 20));

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Integração Financeira de Entrada</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Pagamentos agendados, transmissão CNAB/API e confirmação da liquidação bancária.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-surface-container-lowest rounded-xl border border-outline-variant shadow-sm p-md mb-lg flex flex-wrap items-end gap-md">
        <div className="flex flex-col">
          <label htmlFor="payments-status" className="font-label-sm text-label-sm text-on-surface-variant uppercase mb-xs">Status</label>
          <select
            id="payments-status"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="bg-surface-container-low border border-outline-variant font-body-sm text-body-sm rounded py-2 px-sm focus:ring-1 focus:ring-primary focus:border-primary cursor-pointer min-w-[200px]"
          >
            {STATUS_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <Card variant="outlined" padding="0">
        <CardHeader className="px-md pt-md">
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Pagamentos</h3>
          <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">{data?.total ?? 0} pagamentos</span>
        </CardHeader>

        {paymentsQ.isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
            <span style={{ fontSize: '0.875rem' }}>Carregando pagamentos...</span>
          </div>
        )}

        {paymentsQ.error && !paymentsQ.isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar pagamentos</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>{(paymentsQ.error as Error).message}</p>
          </div>
        )}

        {!paymentsQ.isLoading && !paymentsQ.error && data && data.data.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            Nenhum pagamento encontrado. Agende a primeira remessa para começar.
          </div>
        )}

        {!paymentsQ.isLoading && !paymentsQ.error && data && data.data.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
              <thead>
                <tr>
                  {['Beneficiário', 'Vencimento', 'Valor', 'Método', 'Canal', 'Status', 'Ações'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: h === 'Valor' || h === 'Ações' ? 'right' : 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.data.map((p) => (
                  <tr key={p.id} style={{ transition: 'background 0.15s ease' }} className="payment-row">
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 500 }}>
                      {p.beneficiaryName}
                      {p.documentNumber && (
                        <span style={{ display: 'block', fontSize: '11px', color: '#74777f' }}>Doc: {p.documentNumber}</span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>{formatDate(p.dueDate)}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 600, textAlign: 'right', fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(p.amount)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>{p.paymentMethod ?? '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      <Badge variant={p.channel === 'CNAB' ? 'info' : 'neutral'} size="sm">{PAYMENT_CHANNEL_LABEL[p.channel]}</Badge>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      <Badge variant={paymentStatusBadge(p.status).variant} size="sm" dot>{paymentStatusBadge(p.status).label}</Badge>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                        {p.status === 'SCHEDULED' && (
                          <Button
                            variant="outline"
                            size="sm"
                            loading={transmitMutation.isPending}
                            onClick={() => {
                              void transmitMutation.mutateAsync({ id: p.id, batchId: `LOTE-${new Date().toISOString().slice(0, 10)}` })
                                .catch(() => undefined);
                            }}
                          >
                            Transmitir
                          </Button>
                        )}
                        {(p.status === 'SCHEDULED' || p.status === 'TRANSMITTED') && (
                          <Button variant="primary" size="sm" onClick={() => setConfirmTarget(p)}>
                            Confirmar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!paymentsQ.isLoading && !paymentsQ.error && data && data.total > 20 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
            <Pagination page={page} totalPages={totalPages} onChange={(p) => setPage(p)} />
          </div>
        )}
      </Card>

      {/* Modal de confirmação */}
      {confirmTarget && (
        <ConfirmModal
          payment={confirmTarget}
          onClose={() => setConfirmTarget(null)}
        />
      )}

      <style>{`
        .payment-row:hover td { background: #eceef0; }
      `}</style>
    </div>
  );
}

function ConfirmModal({ payment, onClose }: { payment: ScheduledPayment; onClose: () => void }) {
  const { data: accounts } = useBankAccounts();
  const confirmMutation = useConfirmFinancialEntry();

  const [accountId, setAccountId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId && accounts && accounts.length > 0) setAccountId(accounts[0]?.id ?? '');
  }, [accounts, accountId]);

  const valid = !!accountId;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!valid) return;
    setError(null);
    try {
      await confirmMutation.mutateAsync({
        id: payment.id,
        input: { accountId, ...(notes ? { notes } : {}) },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao confirmar.');
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Confirmar liquidação financeira">
      <form onSubmit={handleSubmit} className="fin-form">
        {/* Summary — espelho do protótipo (Due Date, Value, Status) */}
        <div className="bg-surface-container-low rounded-lg p-md grid grid-cols-3 gap-md">
          <div>
            <p className="fin-label">Vencimento</p>
            <p className="fin-value">{formatDate(payment.dueDate)}</p>
          </div>
          <div>
            <p className="fin-label">Valor</p>
            <p className="fin-value font-bold">{formatCurrency(payment.amount)}</p>
          </div>
          <div>
            <p className="fin-label">Status</p>
            <p className="fin-value">
              <Badge variant={paymentStatusBadge(payment.status).variant} size="sm" dot>
                {PAYMENT_STATUS_LABEL[payment.status]}
              </Badge>
            </p>
          </div>
        </div>

        <p className="fin-context">
          {payment.beneficiaryName} · {payment.paymentMethod} · {PAYMENT_CHANNEL_LABEL[payment.channel]}
          {payment.batchId ? ` · Lote ${payment.batchId}` : ''}
        </p>

        <div className="fin-field">
          <label htmlFor="confirm-account" className="fin-label">Conta de débito</label>
          <select
            id="confirm-account"
            className="fin-select"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            {(accounts ?? []).length === 0 && <option value="">Nenhuma conta cadastrada</option>}
            {(accounts ?? []).map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.name} {acc.bankName ? `· ${acc.bankName}` : ''}
              </option>
            ))}
          </select>
        </div>
        <Input label="Observações" value={notes} onChange={(e) => setNotes(e.target.value)} />

        {error && <p style={{ color: '#ba1a1a', margin: 0, fontSize: '0.875rem' }}>{error}</p>}

        <div className="fin-form-actions">
          <Button variant="outline" type="button" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" type="submit" loading={confirmMutation.isPending} disabled={!valid}>
            Confirmar entrada
          </Button>
        </div>
      </form>

      <style>{`
        .fin-form { display: flex; flex-direction: column; gap: 1rem; }
        .fin-form-actions { display: flex; gap: 0.75rem; justify-content: flex-end; }
        .fin-field { display: flex; flex-direction: column; gap: 0.25rem; }
        .fin-label { margin: 0; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #43474e; }
        .fin-value { margin: 0; font-size: 0.875rem; color: #191c1e; }
        .fin-context { margin: 0; font-size: 0.875rem; color: #43474e; }
        .fin-select { padding: 0.5rem; background: #f7f9fb; border: 1px solid #c4c6cf; border-radius: 2px; font: inherit; font-size: 0.875rem; color: #191c1e; }
        .fin-select:focus { outline: none; border-color: #002045; box-shadow: 0 0 0 1px #002045; }
      `}</style>
    </Modal>
  );
}